'use client'

import { useMutation } from '@tanstack/react-query'
import { capitalize } from 'lodash'

import { SplitsDialogContent } from '~/components/component-library/Dialog'
import { useAuth } from '~/context/AuthProvider'
import { useActiveOrgMember, useRequiredActiveOrg } from '~/hooks/org'
import { PaidPlanName, usePlans } from '~/hooks/usePlans'
import { useServerRequests } from '~/hooks/useRequests'

// Subscriptions are invoiced manually: the endpoint alerts the team, who email
// an invoice to the org owners. Only owners can request one.
export const SubscribeDialog = ({ plan }: { plan: PaidPlanName }) => {
  const { id: orgId } = useRequiredActiveOrg()
  const orgMember = useActiveOrgMember()
  const { plans } = usePlans()
  const { serverPostRequest } = useServerRequests()
  const { closeGlobalModal } = useAuth()
  const label = capitalize(plan)
  const planDetails = plans.find((p) => p.name === plan)
  const isOwner = orgMember?.role === 'OWNER'

  const { mutate: requestUpgrade } = useMutation({
    mutationFn: async () => {
      const result = await serverPostRequest({
        path: '/api/org/plan/subscribe',
        params: { orgId, planId: planDetails?.id },
        errorMessage: 'Failed to request upgrade',
        successMessage: 'Upgrade requested',
        successMessageOptions: {
          description: 'You will receive an invoice within 48 hours.',
        },
      })
      // serverPostRequest swallows errors (and toasts them), so surface the failure here
      if (!result) throw new Error('Failed to request upgrade')
    },
  })

  if (!isOwner) {
    return (
      <SplitsDialogContent
        id="subscribe"
        title={`Subscribe to Splits ${label}`}
        variant="info"
      >
        <p>
          Only workspace owners can request an upgrade. Ask a workspace owner to
          subscribe to Splits {label}.
        </p>
      </SplitsDialogContent>
    )
  }

  return (
    <SplitsDialogContent
      id="subscribe"
      title={`Subscribe to Splits ${label}`}
      variant="cta"
      actionButton={{
        text: 'Request upgrade',
        // Close right away; the request helper toasts the outcome either way.
        onClick: () => {
          requestUpgrade()
          closeGlobalModal()
        },
        isDisabled: planDetails === undefined,
      }}
    >
      <p>
        We&apos;ll email an invoice to your workspace owners within 48 hours.
        Once it&apos;s paid, your plan will be upgraded.
      </p>
    </SplitsDialogContent>
  )
}
